import { forwardRef, useEffect, useMemo, useState } from 'react';
import Grid, {
    GridExposedMethods,
    GridSettings,
    type OnViewChangedCallback,
} from '~/components/Grid';
import { useReportQuery } from '~/lib/Queries.js';
import type { ReportResponse, EventsReportObject } from '~/lib/Api.types';
import { ReportType, TemplateIndex } from '../../ReportsIndexPage/Report.types';
import useColumnsFromKeys from './useColumnsFromKeys';
import { removeBlacklistFields } from './ReportsGrid.utils';
import EventDetailsPanel from '~/components/calendar/EventDetailsPanel';

const ReportDataKeyBlacklist: Array<keyof EventsReportObject> = [
    'event-start-date',
    'event-start-time',
    'client-name',
    'confirmation-url',
];

type EventGridProps = {
    initialSettings?: GridSettings;
    onViewChanged?: OnViewChangedCallback<EventsReportObject>;
    templateSelected?: TemplateIndex;
};
type EventsReport = ReportResponse<EventsReportObject>;
const EventGrid = forwardRef<GridExposedMethods, EventGridProps>(
    ({ initialSettings, onViewChanged, templateSelected }, ref) => {
        const {
            data: formsReport,
            isFetchingNextPage,
            isLoading,
        } = useReportQuery<EventsReportObject>(ReportType.Events);

        const [selectedEvent, setSelectedEvent] = useState<{
            id: number;
        } | null>();
        const filteredFormsReport = useMemo(() => {
            return removeBlacklistFields<EventsReportObject>(
                formsReport,
                ReportDataKeyBlacklist,
            );
        }, [formsReport]);
        const columns = useColumnsFromKeys<EventsReport>(
            filteredFormsReport,
            ReportType.Events,
            templateSelected,
            { setSelectedEvent },
        );

        return (
            <>
                <EventDetailsPanel
                    event={selectedEvent ?? undefined}
                    isOpen={!!selectedEvent}
                    onClose={() => setSelectedEvent(null)}
                />
                <Grid<EventsReportObject>
                    isFetchingMore={isFetchingNextPage}
                    ref={ref}
                    columns={columns}
                    rowData={(formsReport as EventsReport)?.data}
                    initialSettings={initialSettings}
                    onViewChanged={onViewChanged}
                    isLoading={isLoading}
                    showTotalRow={
                        templateSelected ===
                        TemplateIndex.REVENUE_PER_EVENT_TYPE
                    }
                />
            </>
        );
    },
);
export default EventGrid;
